'use client';

import { createContext, useContext } from 'react';
import type { FunnelSession } from './types';

/**
 * What a step left the funnel session as: carry on with `session`, or it was started over.
 * On `start-over` the provider has already emptied the funnel session and sent the user to
 * the entry; the caller just stops.
 */
export type FunnelSessionUpdate =
  | { kind: 'continue'; session: FunnelSession }
  | { kind: 'start-over' };

/**
 * For the trigger files in `features/funnel/model` and `features/recommendations/model`.
 * UI reads answers through `useAnswer` and pending state through `useIsFunnelSubmitting`.
 */
export type FunnelSessionContextValue = {
  session: FunnelSession;
  isPending: boolean;
  /** Replaces the funnel session and writes it to sessionStorage. In-progress input is kept, so unsubmitted picks on other steps survive. */
  commit: (session: FunnelSession) => void;
  /** Like `commit`, but also resets in-progress input to the funnel session's answers. For a goal change, entry redirect, or recovered funnel session. */
  reset: (session: FunnelSession) => void;
  /** Saves the funnel session to the API and applies the outcome to the funnel session. Call it from inside `submit`. */
  saveToApi: (session: FunnelSession, funnelConfigVersion: string) => Promise<FunnelSessionUpdate>;
  /** Fetches the user's last completed funnel session from the API, or null if they have none. */
  recover: () => Promise<FunnelSession | null>;
  /** Queues a task to run after earlier ones finish, inside the submit transition. */
  submit: (task: () => Promise<void>) => void;
};

export const FunnelSessionContext = createContext<FunnelSessionContextValue | undefined>(undefined);

export function useFunnelSession(): FunnelSessionContextValue {
  const ctx = useContext(FunnelSessionContext);
  if (!ctx) throw new Error('useFunnelSession must be used within FunnelSessionProvider');
  return ctx;
}
